import React, {
  MouseEvent,
  RefObject,
  type ReactElement,
  type ReactNode,
} from "react";

import SpinnerLoader from "../../spinner-loader/SpinnerLoader";
import {css, toSentenceCase} from "/src/utils/utils";

import * as styles from "./button.module.scss";

export type ButtonKind = "primary" | "secondary" | "tertiary";
export type ButtonType = "submit" | "reset" | "button";

export interface ButtonProps {
  children: ReactElement<"span"> | string | ReactNode;
  darkMode?: boolean;
  /* This property is used in datadog for ui testing */
  dataTag?: string;
  classes?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  kind?: ButtonKind;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => unknown;
  small?: boolean;
  type?: ButtonType;
  ariaLabel?: string;
  loading?: boolean;
  buttonRef?: RefObject<HTMLButtonElement>;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  [x: string]: any; // any other props
}

const Button = (props: ButtonProps): ReactElement => {
  const {
    children,
    darkMode = false,
    dataTag,
    classes,
    disabled = false,
    fullWidth = false,
    kind = "primary",
    onClick,
    small = false,
    type = "button",
    ariaLabel,
    loading = false,
    buttonRef,
    ...rest
  } = props;

  const shouldDisable = loading || disabled;

  const buttonStyles = css(
    styles.button,
    styles[`button${toSentenceCase(kind)}`],
    darkMode ? styles.buttonDarkmode : "",
    fullWidth ? styles.buttonFullWidth : "",
    disabled ? styles.buttonDisabled : "",
    small ? "p-small" : "p",
    small && kind !== "tertiary" ? styles.buttonSmall : "",
    loading ? styles.buttonLoading : "",
    classes!
  );

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonStyles}
      data-button={dataTag}
      disabled={shouldDisable}
      aria-label={ariaLabel}
      ref={buttonRef}
      {...rest}
    >
      <span className={styles.button__child}>{children}</span>

      {loading ? <SpinnerLoader classes={styles.button__loadingIcon} /> : null}
    </button>
  );
};

export default Button;
